// ============================================================================
// L'INFOBULLE ANCRÉE — la bulle du tableau de bord, posée sur un élément au lieu de suivre le curseur.
//
// ⚠️ POURQUOI UNE SECONDE INFOBULLE. Celle du graphique (`CHART_TIP`) suit le POINTEUR parce que la
// donnée qu'elle décrit change à chaque pixel d'abscisse. Une infobulle d'explication décrit un élément
// FIXE : la faire courir avec la souris la rendrait tremblante et ne dirait plus de QUI elle parle.
// Deux comportements, deux composants — mais UNE SEULE peau (`TIP_SKIN`), pour qu'elles restent de la
// même famille sans qu'on ait à y penser.
//
// ⚠️ ET SURTOUT : elle remplace l'attribut `title` du navigateur, qui n'est pas stylable, apparaît après
// une seconde, et disparaît tout seul au bout de quelques-unes — une explication qu'on n'a pas fini de
// lire. Sur un champ DÉSACTIVÉ, il est en plus purement ignoré par une partie des navigateurs : la
// raison du verrou n'existait alors nulle part.
// ============================================================================
import { Z_LAYERS } from './z';

/**
 * LA PEAU COMMUNE AUX DEUX INFOBULLES — fond, rayon, respiration, échelle de texte.
 *
 * ⚠️ PARTAGÉE PAR CONSTRUCTION, pas par convention : `CHART_TIP` la reprend. Deux jeux de valeurs
 * recopiées auraient divergé à la première retouche, et l'interface aurait porté deux bulles cousines
 * mais pas identiques — le genre d'écart qu'on ne voit jamais côte à côte, donc qu'on ne corrige jamais.
 */
export const TIP_SKIN = {
  minWidth: 132,
  padV: 9,
  padH: 12,
  /** Cran de la rampe de rayons (11 px) — le même que les panneaux ancrés. */
  radius: 'sm',
  /** Surtitre : mono, capitales, espacé — il NOMME ce que la valeur mesure. */
  labelSize: 11,
  labelTracking: '.06em',
  labelOpacity: 0.6,
  /** La valeur, ou la première ligne d'une explication. */
  valueSize: 15,
  /** Ligne de contexte, sous la valeur. */
  subSize: 11.5,
  subOpacity: 0.7,
} as const;

/**
 * Réglages propres à l'infobulle ANCRÉE (le graphique a les siens : décalage au curseur, réserve de bord).
 *
 * ⚠️ `openDelay` N'EST PAS UN CONFORT. La grille des déclinaisons compte des dizaines de tuiles
 * voisines : sans délai, la traverser à la souris ferait clignoter autant de bulles. Fermeture
 * immédiate en revanche — une bulle qui s'attarde masque ce qu'on veut lire ensuite.
 * ⚠️ `textSize` À 12 ET NON 15 : cette bulle-ci porte une PHRASE, pas un montant. L'échelle de la
 * valeur du graphique, appliquée à deux lignes de texte, donnerait un panneau, pas une infobulle.
 */
export const TOOLTIP = {
  gap: 8,
  edgeGap: 10,
  maxWidth: 264,
  arrowHalf: 6,
  /** Rayon des coins en pixels — la pointe ne doit jamais s'y poser, elle y flotterait dans le vide. */
  radiusPx: 11,
  arrowRise: 5,
  textSize: 12,
  lineHeight: 1.45,
  openDelay: 130,
  z: Z_LAYERS.tooltip,
} as const;

/** Le rectangle du déclencheur et la taille mesurée de la bulle, en coordonnées de fenêtre. */
export interface TipRect { left: number; right: number; top: number; bottom: number }
export interface TipSize { width: number; height: number }
export interface TipViewport { width: number; height: number }

export interface TipGeom {
  gap: number;
  edgeGap: number;
  arrowHalf: number;
  /** Rayon des coins : la flèche ne doit jamais se poser dessus, elle y flotterait dans le vide. */
  radiusPx: number;
}

export interface TipPlacement {
  left: number;
  top: number;
  placement: 'above' | 'below';
  /** Abscisse de la POINTE, en coordonnées de la bulle (pas de la fenêtre). */
  arrowX: number;
}

/**
 * Où poser la bulle, et où poser sa pointe.
 *
 * ⚠️ AU-DESSUS PAR DÉFAUT, et pas par goût : posée dessous, la bulle recouvre l'élément SUIVANT — dans
 * une grille dense, celui qu'on s'apprêtait justement à survoler. Elle ne bascule dessous que si le
 * haut de la fenêtre ne la contient pas.
 * ⚠️ LA POINTE SE DÉSOLIDARISE DU CENTRE quand la bulle est bornée au bord de l'écran : sans ce
 * rattrapage, une bulle repoussée vers l'intérieur désignerait un voisin de son déclencheur — soit
 * exactement l'information fausse qu'elle est censée porter.
 */
export function anchorTip(
  trigger: TipRect,
  bulle: TipSize,
  viewport: TipViewport,
  geom: TipGeom,
): TipPlacement {
  const centre = (trigger.left + trigger.right) / 2;
  const left = Math.max(
    geom.edgeGap,
    Math.min(centre - bulle.width / 2, viewport.width - bulle.width - geom.edgeGap),
  );

  const hautSiDessus = trigger.top - geom.gap - bulle.height;
  const tientDessus = hautSiDessus >= geom.edgeGap;
  const top = tientDessus
    ? hautSiDessus
    : Math.min(trigger.bottom + geom.gap, Math.max(geom.edgeGap, viewport.height - bulle.height - geom.edgeGap));

  // La pointe vise le centre du déclencheur, sans jamais quitter la partie DROITE de la bulle.
  const borne = geom.radiusPx + geom.arrowHalf;
  const arrowX = Math.max(borne, Math.min(centre - left, Math.max(borne, bulle.width - borne)));

  return { left, top, placement: tientDessus ? 'above' : 'below', arrowX };
}
